import { useRequest } from 'ahooks';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'components/ui/dialog';
import { Spinner } from 'components/ui/spinner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiRegisteredCaregiversManager from 'services/api/ApiRegisteredCaregiversManager';
import { CaregiverDTO } from 'services/api/models/CaregiverDTO';
import { toast } from 'sonner';
import defaultCaregiverPhoto from '../../assets/experts-man-dark.png';

export type CaregiverProfileModalProps = {
  caregiverId: string;
  open?: boolean;
  onClose?: () => void;
};

const CaregiverProfileModal: React.FC<CaregiverProfileModalProps> = ({
  caregiverId,
  open = false,
  onClose = undefined,
}) => {
  // Hooks
  const { t } = useTranslation();

  // State
  const [isModalOpen, setIsModalOpen] = useState(open);

  const { data: expert, loading } = useRequest(
    () => ApiRegisteredCaregiversManager.getRegisteredCaregiver({ caregiverId }),
    {
      onError: () => {
        toast.error(t('caregiverProfile.errors.fetch'));
      },
    },
  );

  const handleOk = () => {
    setIsModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const displayAddressChunks = (caregiver: CaregiverDTO) =>
    caregiver.address_street || caregiver.address_zip_code || caregiver.address_city;

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleOk();
      }}
    >
      <DialogContent showCloseButton={false} aria-describedby={undefined}>
        {!expert && <DialogTitle className="sr-only">{t('global.loading')}</DialogTitle>}
        <div className="-mt-4 flex h-full flex-col overflow-hidden">
          {loading && (
            <div className="flex justify-center py-6">
              <Spinner className="size-6" />
            </div>
          )}
          {expert && (
            <div className="mx-auto my-2.5 text-center">
              <Avatar size="xl" className="mx-auto">
                <AvatarImage src={expert.user.photo_url || defaultCaregiverPhoto} alt="profile" />
                <AvatarFallback>{expert.caregiver.display_name?.[0]?.toUpperCase() ?? ''}</AvatarFallback>
              </Avatar>
              <DialogTitle asChild>
                <h2>{expert.caregiver.display_name}</h2>
              </DialogTitle>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {expert.caregiver.caregiver_groups.map(group => (
                  <Badge key={group.id} variant="secondary">
                    {group.display_name}
                  </Badge>
                ))}
              </div>
              {displayAddressChunks(expert.caregiver) && (
                <p className="my-1 text-sm text-subtle-foreground">
                  {(expert.caregiver.address_zip_code || expert.caregiver.address_city) && (
                    <>
                      {expert.caregiver.address_zip_code} {expert.caregiver.address_city}
                    </>
                  )}
                </p>
              )}
              <p className="my-6 whitespace-pre-wrap text-left text-sm text-subtle-foreground">
                {expert.caregiver.bio}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={handleOk}>{t('global.buttons.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export default CaregiverProfileModal;
